import React, { useState, useEffect, useRef, createContext } from "react";
import { crud, url } from "../crudRequests";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Avatar,
  Divider,
  Heading,
  Badge,
  Spinner,
  Icon,
  useToast,
  Button,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from "@chakra-ui/react";
import { IoSearch, IoChevronDown, IoChevronForward } from "react-icons/io5";
import { FiHash, FiArrowLeft, FiPlus } from "react-icons/fi";
import { BsChatDots } from "react-icons/bs";
import { Collapse, useDisclosure } from "@chakra-ui/react";
import StaffChatArea from "./StaffChatArea";
import CSSAvatar from "./CSSAvatar";
import GroupManagementModal from "./GroupManagementModal";
import {
  canPatientInitiateNewConversation,
  canPatientRespondToConversation,
  canPatientViewConversation,
} from "./patientMessagingPermissions";

// Context for sharing state between components
export const MessagingContext = createContext();
export const PatientContext = createContext();
export const GroupManagementContext = createContext();

export default function PatientChat({ patient, dashState, dispatch }) {
  const navigate = useNavigate();
  const toast = useToast();
  
  // State management for patient messaging
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Group management modal state
  const [showGroupManagementModal, setShowGroupManagementModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [patientCanInitiate, setPatientCanInitiate] = useState(
    canPatientInitiateNewConversation(patient)
  );
  const [managedGroup, setManagedGroup] = useState(null);
  
  // Socket reference
  const socketRef = useRef(null);
  const isSocketSetup = useRef(false);
  const selectedConversationRef = useRef(null);
  const conversationsRef = useRef([]);
  
  // Collapsible sections
  const { isOpen: isGroupsOpen, onToggle: onGroupsToggle } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isDMsOpen, onToggle: onDMsToggle } = useDisclosure({ defaultIsOpen: true });
  
  // Separate groups and DMs
  const groups = filteredConversations.filter(c => c.isGroup);
  const dms = filteredConversations.filter(c => !c.isGroup);
  
  // Open group management modal
  const openGroupManagement = (group) => {
    setManagedGroup(group);
    setShowGroupManagementModal(true);
  };
  
  // Handle leave group
  const handleLeaveGroup = (groupId) => {
    // Remove the group from conversations list
    setConversations(prev => prev.filter(conv => conv.conversationId !== groupId));
    setFilteredConversations(prev => prev.filter(conv => conv.conversationId !== groupId));
    
    // Clear selection if this was the selected conversation
    if (selectedConversation?.conversationId === groupId) {
      setSelectedConversation(null);
    }
    
    // Reload conversations to sync with backend
    loadPatientConversations();
  };
  
  // Keep ref in sync
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  
  // Load patient conversations
  useEffect(() => {
    if (patient?.pid) {
      loadPatientConversations();
    }
  }, [patient?.pid]);
  
  // Setup socket ONCE
  useEffect(() => {
    if (!patient?.pid || isSocketSetup.current) return;
    
    // Mark as setup to prevent multiple connections
    isSocketSetup.current = true;
    
    // Connect to chat namespace with proper auth
    // The session travels with the connection: the chat namespace takes the
    // person and the clinic from it, never from `auth.userId`.
    const socket = io(url + "/chat", {
      withCredentials: true,
      auth: {
        token: dashState?.jwt,
        rooms: [`user_${patient.pid}`],
        userId: patient.pid
      }
    });
    
    socketRef.current = socket;
    
    // Listen for being added to a group
    socket.on("added-to-group", (groupData) => {
      toast({
        title: "Added to group",
        description: `You've been added to "${groupData.groupName}"`,
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      
      loadPatientConversations();
    });
    
    // Listen for group updates
    socket.on("group-updated", (groupData) => {
      // Check if patient is part of this group
      const isPatientInGroup = groupData.usersId?.includes(patient.pid);
      
      if (isPatientInGroup) {
        // Check if this is a new group for the patient
        const existingGroup = conversationsRef.current.find(
          conv => conv.conversationId === groupData.conversationId
        );
        
        if (!existingGroup) {
          // This is a NEW group for the patient - add it to conversations
          const newGroup = {
            conversationId: groupData.conversationId,
            isGroup: true,
            groupName: groupData.groupName || groupData.displayName,
            displayName: groupData.groupName || groupData.displayName,
            memberCount: groupData.usersId?.length || 0,
            members: groupData.usersMeta,
            usersId: groupData.usersId,
            lastMessage: groupData.lastMessage || null,
            unreadCount: 0,
            timestamp: Date.now()
          };
          
          setConversations(prev => [newGroup, ...prev]);
          setFilteredConversations(prev => [newGroup, ...prev]);
          
          // Join the room for real-time updates
          socket.emit("join-room", groupData.conversationId);
          
          toast({
            title: "Added to group",
            description: `You've been added to "${groupData.groupName || groupData.displayName}"`,
            status: "info",
            duration: 4000,
            isClosable: true,
          });
        } else {
          // Update existing group
          setConversations(prev => 
            prev.map(conv => {
              if (conv.conversationId === groupData.conversationId) {
                return {
                  ...conv,
                  groupName: groupData.groupName,
                  displayName: groupData.groupName || groupData.displayName,
                  memberCount: groupData.usersId?.length || 0,
                  members: groupData.usersMeta,
                  usersId: groupData.usersId
                };
              }
              return conv;
            })
          );
          
          setFilteredConversations(prev =>
            prev.map(conv => {
              if (conv.conversationId === groupData.conversationId) {
                return {
                  ...conv,
                  groupName: groupData.groupName,
                  displayName: groupData.groupName || groupData.displayName,
                  memberCount: groupData.usersId?.length || 0,
                  members: groupData.usersMeta,
                  usersId: groupData.usersId
                };
              }
              return conv;
            })
          );
          
          if (selectedConversationRef.current?.conversationId === groupData.conversationId) {
            setSelectedConversation(prev => ({
              ...prev,
              groupName: groupData.groupName,
              displayName: groupData.groupName || groupData.displayName,
              memberCount: groupData.usersId?.length || 0,
              members: groupData.usersMeta,
              usersId: groupData.usersId
            }));
          }
        }
      }
    });
    
    // Listen for being removed from group
    socket.on("group-member-removed", (data) => {
      if (data.removedMemberId === patient.pid) {
        setConversations(prev => 
          prev.filter(conv => conv.conversationId !== data.groupId)
        );
        
        setFilteredConversations(prev => 
          prev.filter(conv => conv.conversationId !== data.groupId)
        );
        
        if (selectedConversationRef.current?.conversationId === data.groupId) {
          setSelectedConversation(null);
        }
        
        toast({
          title: "Removed from group",
          description: "You have been removed from the group",
          status: "warning",
          duration: 4000,
        });
      } else {
        // Update group member count
        const updatedGroup = data.updatedGroup;
        
        setConversations(prev =>
          prev.map(conv => {
            if (conv.conversationId === data.groupId) {
              return {
                ...conv,
                memberCount: updatedGroup.memberCount,
                members: updatedGroup.usersMeta,
                usersId: updatedGroup.usersId
              };
            }
            return conv;
          })
        );
        
        setFilteredConversations(prev =>
          prev.map(conv => {
            if (conv.conversationId === data.groupId) {
              return {
                ...conv,
                memberCount: updatedGroup.memberCount,
                members: updatedGroup.usersMeta,
                usersId: updatedGroup.usersId
              };
            }
            return conv;
          })
        );
      }
    });
    
    // Listen for member leaving group voluntarily
    socket.on("group-member-left", (data) => {
      // Update group member count and members list
      setConversations(prev =>
        prev.map(conv => {
          if (conv.conversationId === data.groupId) {
            const updatedUsersMeta = { ...conv.members };
            delete updatedUsersMeta[data.leftMemberId];
            
            return {
              ...conv,
              memberCount: (conv.memberCount || 0) - 1,
              members: updatedUsersMeta,
              usersId: conv.usersId?.filter(id => id !== data.leftMemberId)
            };
          }
          return conv;
        })
      );
      
      setFilteredConversations(prev =>
        prev.map(conv => {
          if (conv.conversationId === data.groupId) {
            const updatedUsersMeta = { ...conv.members };
            delete updatedUsersMeta[data.leftMemberId];
            
            return {
              ...conv,
              memberCount: (conv.memberCount || 0) - 1,
              members: updatedUsersMeta,
              usersId: conv.usersId?.filter(id => id !== data.leftMemberId)
            };
          }
          return conv;
        })
      );
      
      // Update selected conversation if it's this group
      if (selectedConversationRef.current?.conversationId === data.groupId) {
        setSelectedConversation(prev => {
          const updatedUsersMeta = { ...prev.members };
          delete updatedUsersMeta[data.leftMemberId];
          
          return {
            ...prev,
            memberCount: (prev.memberCount || 0) - 1,
            members: updatedUsersMeta,
            usersId: prev.usersId?.filter(id => id !== data.leftMemberId)
          };
        });
      }
    });
    
    // Listen for incoming messages to update sidebar
    socket.on("message", (messageData) => {
      setConversations(prev => 
        prev.map(conv => {
          if (conv.conversationId === messageData.conversationId) {
            return {
              ...conv,
              lastMessage: {
                message: messageData.message,
                senderId: messageData.senderId,
                timestamp: messageData.timestamp,
                messageType: messageData.messageType,
                attachment: messageData.attachment,
              },
              unreadCount: selectedConversationRef.current?.conversationId === messageData.conversationId 
                ? 0 
                : (conv.unreadCount || 0) + 1,
            };
          }
          return conv;
        })
      );
      
      setFilteredConversations(prev =>
        prev.map(conv => {
          if (conv.conversationId === messageData.conversationId) {
            return {
              ...conv,
              lastMessage: {
                message: messageData.message,
                senderId: messageData.senderId,
                timestamp: messageData.timestamp,
                messageType: messageData.messageType,
                attachment: messageData.attachment,
              },
              unreadCount: selectedConversationRef.current?.conversationId === messageData.conversationId 
                ? 0 
                : (conv.unreadCount || 0) + 1,
            };
          }
          return conv;
        })
      );
    });
    
    // Listen for new conversations
    socket.on("new-conversation", (conversationData) => {
      // Only process conversations that involve this patient
      if (conversationData.usersId.includes(patient.pid)) {
        loadPatientConversations();
        
        toast({
          title: "New conversation",
          description: conversationData.isGroup 
            ? `Added to ${conversationData.groupName}` 
            : "New message received",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
        
        // Join the conversation room
        socket.emit("join-room", conversationData.conversationId);
      }
    });
    
    // Listen for chat clearing
    socket.on("chat-cleared", (data) => {
      const { conversationId, clearedAt, clearedBy } = data;
      
      setConversations(prev => 
        prev.map(conv => {
          if (conv.conversationId === conversationId) {
            return {
              ...conv,
              lastMessage: null,
              clearedAt: clearedAt
            };
          }
          return conv;
        })
      );
      
      setFilteredConversations(prev =>
        prev.map(conv => {
          if (conv.conversationId === conversationId) {
            return {
              ...conv,
              lastMessage: null,
              clearedAt: clearedAt
            };
          }
          return conv;
        })
      );
      
      if (selectedConversationRef.current?.conversationId === conversationId && clearedBy !== patient.pid) {
        toast({
          title: "Chat cleared",
          description: "An admin has cleared all messages in this chat",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      }
    });
    
    // Listen for group deletion
    socket.on("group-deleted", (groupId) => {
      toast({
        title: "Group deleted",
        description: "A group you were part of has been deleted",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      
      loadPatientConversations();
    });
    
    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        isSocketSetup.current = false;
      }
    };
  }, [patient?.pid]);
  
  // Load patient conversations
  // Debug: Check patient messaging permissions

  
  // Handle new chat button click
  const handleNewChat = async () => {
    if (!patientCanInitiate) return;

    setLoadingDoctors(true);
    
    try {
   // Filter out staff who already have conversations with the patient
   let existingConvIds = conversations
   .filter(conv => !conv.isGroup).map(conv => conv.usersId)
   .flat()

    existingConvIds = [...new Set(existingConvIds)];
    const patientDoctorIds = Array.isArray(patient.did) ? patient.did : (patient.did ? [patient.did] : []);


      // Fetch all doctors and admins
      const requests = [
        {
          db: dashState.db,
          collection: "doctors",
          parameters: [{did: {$nin: existingConvIds, $in: patientDoctorIds}}],
          method: "find",
        },
        {
          db: dashState.db,
          collection: "admins",
          parameters: [{id: {$nin: existingConvIds}}],
          method: "find",
        }
      ];
      
      const responses = await crud(dashState, requests);
      
      // Process all doctors
      const allDoctors = responses.data?.[0] || [];
      
      const assignedDoctors = allDoctors.map(doc => {
        const docName = doc.name || `${doc.fName || ''} ${doc.lName || ''}`.trim() || 'Clinician';
      return {
          id: doc.did,
          name: docName,
          userType: 'doctor',
          photo: doc.photo,
          specialty: doc.specialty || 'General Practice',
          isAssigned: true,
          isPrimary: patient.preferredClinicianId === doc.did
        };
        

      });
      
      // Process admins - they can always be contacted
      const adminsList = responses.data?.[1] || [];
      const admins = adminsList.map(admin => {
        const adminName = admin.name || `${admin.fName || ''} ${admin.lName || ''}`.trim() || 'Admin';
        return {
          id: admin.id || admin._id,
          name: adminName,
          userType: 'admin',
          photo: admin.photo,
        };
      });
      
    
   
      // Combine only assigned doctors and admins (NO other doctors)
      const availableStaff = [...assignedDoctors, ...admins]
      
      setAvailableDoctors(availableStaff);
      setShowNewChatModal(true);
      
    } catch (error) {
      console.error("Error loading available staff:", error);
      toast({
        title: "Error loading contacts",
        description: "Could not load available staff members",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingDoctors(false);
    }
  };
  
  // Handle starting a new conversation
  const handleStartConversation = (staffMember) => {
    if (!patientCanInitiate) return;

    setShowNewChatModal(false);
    // Generate conversationId immediately to avoid null ID issues
    const {id,name,photo,userType} = staffMember;
    const currentUserId = patient.pid;
    const generatedId = [id,currentUserId].join('_');

    // Create a conversation object for the selected staff member
    const newConversation = {
      conversationId: generatedId, // Generated ID instead of null
      usersId: [currentUserId, staffMember.id],
      lid: dashState.selectedLocation,
      messages: [],
      usersMeta: {
        [id]: {
          name: name,
          photo: photo,
          userType: userType,
        },
        [currentUserId]: {
          name: `${patient.fName || ''} ${patient.lName || ''}`.trim(),
          photo: patient.photo||'',
          userType: 'patient',
        },
      },
      context: "patient-chat",
      timestamp: Date.now(),
      isGroup: false,
      lastMessage: null,
      unreadCount: 0,
    };

    setSelectedConversation(newConversation);
  };





  const loadPatientConversations = async () => {
    setLoading(true);
    try {
      // Load all conversations where patient is included
      // Remove context filter to see ALL patient conversations
      const response = await crud(dashState, [
        {
          db: dashState.db,
          collection: "conversations",
          parameters: [{ 
            usersId: patient.pid
          }],
          method: "find",
        }
      ]);
      
      const convs = (response.data?.[0] || []).filter((conv) =>
        canPatientViewConversation(conv, patient.pid)
      );
      
      // Format conversations for display
      const formattedConversations = convs.map(conv => {
        if (conv.isGroup) {
          // Group conversation
          return {
            id: conv.conversationId,
            conversationId: conv.conversationId,
            displayName: conv.groupName || "Unnamed Group",
            fullName: conv.groupName || "Unnamed Group",
            isGroup: true,
            lastMessage: conv.messages?.[conv.messages.length - 1],
            unreadCount: 0,
            memberCount: conv.usersId?.length || 0,
            members: conv.usersMeta,
            usersId: conv.usersId,
            createdAt: conv.createdAt,
            timestamp: conv.timestamp,
          };
        } else {
          // Individual conversation with staff member
          // Find the other participant (not the patient) by checking usersMeta keys
          const otherUserId = Object.keys(conv.usersMeta||{}).find(id => id !== patient.pid);
          const clinicianData = otherUserId ? conv.usersMeta[otherUserId] : null;

          return {
            ...conv,
            displayName: clinicianData?.name || "Unknown",
            fullName: clinicianData?.name || "Unknown",
            photo: clinicianData?.photo,
            userType: clinicianData?.userType,
            lastMessage: conv.messages?.[conv.messages.length - 1],
          };
        }
      });
      
      // Sort: groups first, then by last message
      const sorted = formattedConversations.sort((a, b) => {
        if (a.isGroup && !b.isGroup) return -1;
        if (!a.isGroup && b.isGroup) return 1;
        
        const aTime = a.lastMessage?.timestamp || a.timestamp || 0;
        const bTime = b.lastMessage?.timestamp || b.timestamp || 0;
        
        return new Date(bTime) - new Date(aTime);
      });
      
      setConversations(sorted);
      setFilteredConversations(sorted);
      
      // Join all conversation rooms for real-time updates
      if (socketRef.current && socketRef.current.connected) {
        convs.forEach(conv => {
          socketRef.current.emit("join-room", conv.conversationId);
        });
      }
      
    } catch (error) {
      console.error("Error loading conversations:", error);
      toast({
        title: "Error loading conversations",
        description: "Please try refreshing the page",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Keep conversationsRef in sync with conversations state
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);
  
  // Keep selectedConversationRef in sync
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  
  // Load patient settings including messaging permissions
  const loadPatientSettings = async () => {
    try {
      const response = await crud(dashState, [
        {
          db: dashState.db,
          collection: "patients",
          parameters: [{ pid: patient.pid }],
          method: "findOne",
        }
      ]);
      
      if (response.data?.[0]) {
        const updatedPatient = response.data[0];
        
        // Update local state
        setPatientCanInitiate(canPatientInitiateNewConversation(updatedPatient));
        
        // Update patient in parent state if dispatch is available
        if (dispatch) {
          dispatch({
            type: "SET_PATIENT", 
            payload: updatedPatient
          });
        }
      }
    } catch (error) {
      console.error("Error loading patient settings:", error);
    }
  };

  // Load conversations on mount
  useEffect(() => {
    if (patient?.pid && dashState?.jwt) {
      loadPatientConversations();
      // Also load latest patient settings to get messaging permissions
      loadPatientSettings();
    }
  }, [patient?.pid, dashState?.jwt]);
  
  // Filter conversations based on search
  useEffect(() => {
    if (searchTerm) {
      const filtered = conversations.filter(conv => {
        const displayName = conv.displayName?.toLowerCase() || "";
        const fullName = conv.fullName?.toLowerCase() || "";
        
        return (
          displayName.includes(searchTerm.toLowerCase()) ||
          fullName.includes(searchTerm.toLowerCase())
        );
      });
      setFilteredConversations(filtered);
    } else {
      setFilteredConversations(conversations);
    }
  }, [searchTerm, conversations]);
  
  return (
    <Flex w="100%" h="100%">
      {/* Sidebar */}
      <Box
        w={{ base: selectedConversation ? "0" : "100%", md: "280px" }}
        minW={{ md: "280px" }}
        h="100%"
        borderRight="1px solid"
        borderColor="gray.100"
        bg="white"
        display={{ base: selectedConversation ? "none" : "flex", md: "flex" }}
        flexDirection="column"
      >
        {/* Header */}
        <Box px={4} pt={5} pb={4} borderBottom="1px solid" borderColor="gray.100">
          <Box mb={4}>
            <Text fontSize="lg" fontWeight="600" color="#1a1f36">
              Messages
            </Text>
            <Text fontSize="12px" color="#6b7280" mt={0.5}>
              {patient?.fName} {patient?.lName}
            </Text>
          </Box>

          {/* Search Bar */}
          <InputGroup
            size="sm"
            mb={0}
            sx={{
              '& .search-icon': {
                color: 'gray.300',
                transition: 'color 0.2s'
              },
              '&:focus-within .search-icon': {
                color: 'gray.500'
              }
            }}
          >
            <InputLeftElement pointerEvents="none" h="34px">
              <IoSearch className="search-icon" />
            </InputLeftElement>
            <Input
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="gray.50"
              border="1px solid"
              borderColor="gray.100"
              fontSize="13px"
              borderRadius="lg"
              h="34px"
              pl="36px"
              _placeholder={{ color: "gray.400" }}
              _hover={{
                borderColor: "gray.200",
              }}
              _focus={{
                borderColor: "#5754FF",
                bg: "white",
                boxShadow: "0 0 0 1px #5754FF"
              }}
            />
          </InputGroup>
        </Box>
        
        {/* Conversations List */}
        <VStack
          flex={1}
          overflowY="auto"
          spacing={0}
          align="stretch"
          bg="white"
          px={2}
          py={2}
          className="sbar3"
        >
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="sm" color="gray.300" thickness="2px" />
            </Flex>
          ) : conversations.length === 0 ? (
            <Flex
              flex={1}
              align="center"
              justify="center"
              direction="column"
              px={4}
            >
              <Box
                w="48px"
                h="48px"
                borderRadius="full"
                bg="gray.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={3}
              >
                <Icon as={BsChatDots} fontSize="20px" color="gray.300" />
              </Box>
              <Text fontSize="13px" fontWeight="500" color="gray.600" textAlign="center">
                No conversations yet
              </Text>
              <Text fontSize="12px" color="gray.400" mt={1} textAlign="center">
                {patientCanInitiate
                  ? "Start a conversation with your care team"
                  : "Your care team will message you here"
                }
              </Text>
              {patientCanInitiate && (
                <Button
                  size="sm"
                  bg="#5754FF"
                  color="white"
                  leftIcon={<FiPlus size={14} />}
                  onClick={handleNewChat}
                  mt={4}
                  fontWeight="500"
                  _hover={{ bg: "#4644e0" }}
                  _active={{ bg: "#3a38c8" }}
                >
                  New conversation
                </Button>
              )}
            </Flex>
          ) : (
            <>
              {/* Groups Section */}
              {groups.length > 0 && (
                <Box mb={1}>
                  <HStack
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    borderRadius="md"
                    onClick={onGroupsToggle}
                    justify="space-between"
                  >
                    <HStack spacing={1.5}>
                      <Icon
                        as={isGroupsOpen ? IoChevronDown : IoChevronForward}
                        fontSize="10px"
                        color="gray.400"
                      />
                      <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">
                        Groups
                      </Text>
                      <Text fontSize="11px" color="gray.400">
                        {groups.length}
                      </Text>
                    </HStack>
                  </HStack>

                  <Collapse in={isGroupsOpen}>
                    <VStack spacing={0} align="stretch" mt={0.5}>
                      {groups.map((group) => {
                        const isActive = selectedConversation?.conversationId === group.conversationId;
                        return (
                          <HStack
                            key={group.conversationId}
                            px={3}
                            py={2}
                            cursor="pointer"
                            bg={isActive ? "#f0f0ff" : "transparent"}
                            _hover={{ bg: isActive ? "#f0f0ff" : "gray.50" }}
                            borderRadius="md"
                            onClick={() => setSelectedConversation(group)}
                            spacing={2.5}
                            transition="background 0.1s"
                          >
                            <Box
                              w="24px"
                              h="24px"
                              borderRadius="md"
                              bg={isActive ? "#5754FF" : "gray.100"}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <Icon as={FiHash} color={isActive ? "white" : "gray.500"} fontSize="11px" />
                            </Box>
                            <Text
                              fontSize="13px"
                              fontWeight={isActive ? "500" : "normal"}
                              color={isActive ? "#1a1f36" : "gray.700"}
                              flex={1}
                              noOfLines={1}
                            >
                              {group.displayName}
                            </Text>
                            {(group.unreadCount || 0) > 0 && (
                              <Box
                                bg="#5754FF"
                                color="white"
                                fontSize="10px"
                                fontWeight="600"
                                px={1.5}
                                py={0.5}
                                borderRadius="full"
                                minW="18px"
                                textAlign="center"
                              >
                                {group.unreadCount}
                              </Box>
                            )}
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Collapse>
                </Box>
              )}

              {/* Direct Messages Section */}
              {(dms.length > 0 || patientCanInitiate) && (
                <Box>
                  <HStack
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    borderRadius="md"
                    onClick={onDMsToggle}
                    justify="space-between"
                  >
                    <HStack spacing={1.5} flex={1}>
                      <Icon
                        as={isDMsOpen ? IoChevronDown : IoChevronForward}
                        fontSize="10px"
                        color="gray.400"
                      />
                      <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">
                        Direct Messages
                      </Text>
                      <Text fontSize="11px" color="gray.400">
                        {dms.length}
                      </Text>
                    </HStack>
                    {patientCanInitiate && (
                      <Tooltip label="New conversation" fontSize="xs" placement="top">
                        <IconButton
                          icon={<FiPlus />}
                          size="xs"
                          variant="ghost"
                          color="gray.500"
                          _hover={{ bg: "gray.100", color: "#5754FF" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNewChat();
                          }}
                          aria-label="New conversation"
                        />
                      </Tooltip>
                    )}
                  </HStack>

                  <Collapse in={isDMsOpen}>
                    <VStack spacing={0} align="stretch" mt={0.5}>
                      {dms.length > 0 ? (
                        dms.map((conv) => {
                          const isActive = selectedConversation?.conversationId === conv.conversationId;
                          return (
                            <HStack
                              key={conv.conversationId}
                              px={3}
                              py={2}
                              cursor="pointer"
                              bg={isActive ? "#f0f0ff" : "transparent"}
                              _hover={{ bg: isActive ? "#f0f0ff" : "gray.50" }}
                              borderRadius="md"
                              onClick={() => setSelectedConversation(conv)}
                              spacing={2.5}
                              transition="background 0.1s"
                            >
                              <CSSAvatar user={conv} size="xs" />
                              <Box flex={1} minW={0}>
                                <Text
                                  fontSize="13px"
                                  fontWeight={isActive ? "500" : "normal"}
                                  color={isActive ? "#1a1f36" : "gray.700"}
                                  noOfLines={1}
                                >
                                  {conv.displayName}
                                </Text>
                                {conv.lastMessage && (
                                  <Text fontSize="11px" color="gray.400" noOfLines={1}>
                                    {conv.lastMessage.messageType === "file"
                                      ? "Sent a file"
                                      : conv.lastMessage.message || ""}
                                  </Text>
                                )}
                              </Box>
                              {(conv.unreadCount || 0) > 0 && (
                                <Box
                                  bg="#5754FF"
                                  color="white"
                                  fontSize="10px"
                                  fontWeight="600"
                                  px={1.5}
                                  py={0.5}
                                  borderRadius="full"
                                  minW="18px"
                                  textAlign="center"
                                >
                                  {conv.unreadCount}
                                </Box>
                              )}
                            </HStack>
                          );
                        })
                      ) : (
                        <Box px={3} py={4}>
                          <Text fontSize="12px" color="gray.400" textAlign="center">
                            No direct messages
                          </Text>
                          {patientCanInitiate && (
                            <Button
                              size="xs"
                              variant="ghost"
                              color="#5754FF"
                              leftIcon={<FiPlus />}
                              onClick={handleNewChat}
                              mt={2}
                              w="full"
                              fontWeight="500"
                              _hover={{ bg: "#f0f0ff" }}
                            >
                              Start a conversation
                            </Button>
                          )}
                        </Box>
                      )}
                    </VStack>
                  </Collapse>
                </Box>
              )}
            </>
          )}
        </VStack>
      </Box>
      
      {/* Right Panel - Chat Area */}
      <Box flex={1} bg="white" position="relative">
        {selectedConversation ? (
          <MessagingContext.Provider value={{ schState: dashState, dispatch }}>
            <GroupManagementContext.Provider value={{ openGroupManagement }}>
              <PatientContext.Provider value={[selectedConversation, setSelectedConversation]}>
                <StaffChatArea
                  key={`${selectedConversation.conversationId}-${selectedConversation.memberCount || 0}`}
                  user={selectedConversation}
                  onBack={() => setSelectedConversation(null)}
                  loadConversations={loadPatientConversations}
                  isPatientView={true}
                  currentUserId={patient.pid}
                  canSendMessage={canPatientRespondToConversation(
                    selectedConversation,
                    patient.pid
                  )}
                />
              </PatientContext.Provider>
            </GroupManagementContext.Provider>
          </MessagingContext.Provider>
        ) : (
          <Flex h="full" align="center" justify="center" direction="column">
            <Box
              w="64px"
              h="64px"
              borderRadius="full"
              bg="gray.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={4}
            >
              <Icon as={BsChatDots} fontSize="24px" color="gray.300" />
            </Box>
            <Text fontSize="15px" fontWeight="500" color="#1a1f36">
              Select a conversation
            </Text>
            <Text fontSize="13px" color="#6b7280" mt={1}>
              Choose from the sidebar to start messaging
            </Text>
          </Flex>
        )}
      </Box>
      
      {/* Group Management Modal */}
      {showGroupManagementModal && managedGroup && (
        <GroupManagementModal
          isOpen={showGroupManagementModal}
          onClose={() => {
            setShowGroupManagementModal(false);
            setManagedGroup(null);
          }}
          group={managedGroup}
          currentUserId={patient.pid}
          patient={patient}
          dashState={dashState}
          onLeaveGroup={handleLeaveGroup}
        />
      )}
      
      {/* New Chat Modal */}
      <Modal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        size="md"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(2px)" />
        <ModalContent maxH="80vh" borderRadius="xl" overflow="hidden">
          <ModalHeader
            fontSize="16px"
            fontWeight="600"
            color="#1a1f36"
            borderBottom="1px solid"
            borderColor="gray.100"
            pb={3}
            pt={5}
          >
            New conversation
          </ModalHeader>
          <ModalCloseButton top={4} />

          <ModalBody
            py={4}
            overflowY="auto"
            maxH="60vh"
            className="sbar3"
          >
            {loadingDoctors ? (
              <Flex justify="center" py={8}>
                <Spinner size="sm" color="gray.300" thickness="2px" />
              </Flex>
            ) : availableDoctors.length > 0 ? (
              <VStack spacing={2} align="stretch">
                <Text fontSize="13px" color="#6b7280" mb={2}>
                  Select a staff member to message
                </Text>

                {!availableDoctors.some(s => s.isAssigned && s.userType === 'doctor') && (
                  <Box
                    px={3}
                    py={2.5}
                    bg="gray.50"
                    borderRadius="lg"
                    borderLeft="3px solid"
                    borderLeftColor="gray.300"
                  >
                    <Text fontSize="12px" color="gray.500">
                      No primary doctor assigned
                    </Text>
                  </Box>
                )}

                {availableDoctors.filter(s => s.isAssigned && s.userType === 'doctor').length > 0 && (
                  <>
                    <Text fontSize="11px" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mt={1}>
                      Your Doctors
                    </Text>
                    {availableDoctors
                      .filter(s => s.isAssigned && s.userType === 'doctor')
                      .map((staff) => (
                        <HStack
                          key={staff.id}
                          px={3}
                          py={2.5}
                          bg="white"
                          border="1px solid"
                          borderColor={staff.isPrimary ? "green.100" : "gray.100"}
                          borderRadius="lg"
                          cursor="pointer"
                          _hover={{
                            bg: "gray.50",
                            borderColor: staff.isPrimary ? "green.200" : "gray.200"
                          }}
                          transition="all 0.15s"
                          onClick={() => handleStartConversation(staff)}
                        >
                          <CSSAvatar
                            user={{
                              name: staff.name,
                              photo: staff.photo
                            }}
                            size="sm"
                          />
                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontSize="13px" fontWeight="500" color="#1a1f36">
                              {staff.name}
                            </Text>
                            <HStack spacing={2}>
                              <Text fontSize="11px" color="#6b7280">
                                {staff.specialty || 'Clinician'}
                              </Text>
                              {staff.isPrimary && (
                                <Badge
                                  bg="green.50"
                                  color="green.700"
                                  fontSize="10px"
                                  px={1.5}
                                  borderRadius="md"
                                  fontWeight="500"
                                >
                                  Primary
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                        </HStack>
                      ))
                    }
                  </>
                )}

              </VStack>
            ) : (
              <Box py={8} textAlign="center">
                <Text fontSize="13px" color="gray.500">
                  No available staff to message
                </Text>
                <Text fontSize="12px" color="gray.400" mt={1}>
                  All your assigned doctors already have conversations with you
                </Text>
              </Box>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.100" pt={3} pb={3}>
            <Button
              variant="ghost"
              onClick={() => setShowNewChatModal(false)}
              fontSize="13px"
              color="gray.600"
              fontWeight="500"
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
